/* eslint-disable @typescript-eslint/naming-convention */
import {Box, Col, Row} from 'components/Grid';
import React, {FC} from 'react';
import style from './style.module.scss';
import {CatalogProductList} from 'modules/products/components/CatalogProductList';
import {Filters} from 'plugins/modules/filter/components/Filters/LazyFilters';
import {MODALS} from 'modules/modals/constants';
import {Modal} from 'modules/modals/components/Modal';
import dynamic from 'next/dynamic';
import {ModalLazyLoader} from 'components/ModalLazyLoader';
import {FilterPickupModalProvider} from 'plugins/modules/filter/provider/FilterPickupModalProvider';
import {PageHead} from 'core/modules/PageHead';
import {ClientDesktopOnly} from 'components/ClientDesktopOnly';
import {CatalogListHeader} from 'modules/products/components/CatalogListHeader';
import {Banner} from 'modules/stock-x/components/Banner';
import {TrustSlider} from 'modules/slider/components/TrustSlider';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';
import {CatalogListHeaderSticky} from 'modules/products/components/CatalogListHeaderSticky';
import {ClientNotMobile} from 'components/ClientNotMobile';
import {useStockXPageState} from 'modules/stock-x/hooks/useStockXPageState';
import Head from 'next/head';
import {TabList} from 'modules/stock-x/components/TabList';
import {IStock} from 'modules/stock-x/types';
import {Breadcrumbs} from 'modules/breadcrumbs/components/Breadcrumbs';
import {IBreadcrumb} from 'modules/seo/types';
import {metaData} from 'core/modules/PageHead/constants';
import {isUrlChangedIgnoringFilters} from 'plugins/modules/filter/helpers';

const StockTermsModal = dynamic(() => import('modules/stock-x/components/StockTermsModal'), {
    loading: () => <ModalLazyLoader />,
});

const FiltersModalMobile = dynamic(() => import('plugins/modules/filter/components/FiltersModalMobile'), {
    loading: () => <ModalLazyLoader />,
});

const ProductPickUpModal = dynamic(() => import('modules/products/components/PickUpModal'), {
    loading: () => <ModalLazyLoader />,
});

const StocksSlider = dynamic(() => import('modules/stock-x/components/StocksSlider/StocksSlider'), {
    loading: () => null,
});

export const StockXPageContent: FC = () => {
    const {seo, isListFetching, isFiltersLoading, ogImage, ogUrl, productSchemaJson, breadcrumbsList, tabs, stockCode} =
        useStockXPageState();

    return (
        <>
            <PageHead
                title={metaData.stock.detail.title(seo?.metaTitle ?? metaData.stock.discount.title)}
                description={metaData.stock.detail.description(
                    seo?.metaDescription ?? metaData.stock.discount.description,
                )}
                imageUrl={ogImage}
                ogUrl={ogUrl}
                robots={seo?.robots}
            />

            {productSchemaJson && (
                <Head>
                    <script
                        type={'application/ld+json'}
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(productSchemaJson),
                        }}
                    />
                </Head>
            )}

            <BreadcrumbsSection breadcrumbsList={breadcrumbsList} isFetching={isListFetching} />

            <Banner />

            <TabListSection tabs={tabs} />

            <CatalogListHeaderSection isListFetching={isListFetching} />

            <ProductListingSection isFiltersLoading={isFiltersLoading} />

            <StocksSlider className={style.stocksSlider} withoutCode={stockCode} />

            <TrustSlider gtagEventsEnabled={true} />

            <ModalsSection />
        </>
    );
};

const BreadcrumbsSection: FC<{
    breadcrumbsList: IBreadcrumb[];
    isFetching: boolean;
}> = ({breadcrumbsList, isFetching}) => (
    <Box commonPaddingOnMobileTablet={true}>
        <Row>
            <Col sm={12}>
                <SkeletonWrapper disabled={!isFetching}>
                    <Breadcrumbs breadcrumbs={breadcrumbsList} />
                </SkeletonWrapper>
            </Col>
        </Row>
    </Box>
);

const CatalogListHeaderSection: FC<{isListFetching: boolean}> = ({isListFetching}) => (
    <SkeletonWrapper disabled={!isListFetching} className={style.mobileHeader}>
        <CatalogListHeader />
    </SkeletonWrapper>
);

const TabListSection: FC<{tabs: IStock['tabs']}> = ({tabs}) => (
    <div className={style.tabs}>
        <TabList tabs={tabs} />
    </div>
);

const ProductListingSection: FC<{
    isFiltersLoading?: boolean;
}> = ({isFiltersLoading}) => (
    <Box className={style.listBox}>
        <SkeletonWrapper className={style.desktopCol} disabled={!isFiltersLoading}>
            <ClientDesktopOnly>
                <Filters />
            </ClientDesktopOnly>
        </SkeletonWrapper>
        <div className={style.contentProduct}>
            <ClientNotMobile>
                <CatalogListHeaderSticky />
            </ClientNotMobile>
            <CatalogProductList />
        </div>
    </Box>
);

const ModalsSection: FC = () => (
    <>
        <Modal name={MODALS.STOCK_X_TERMS_MODAL.name} willMount={true}>
            <StockTermsModal />
        </Modal>

        <Modal
            disableOnPageLoading={false}
            name={MODALS.FILTER_MODAL_MOBILE.name}
            shouldCloseOnUrlChange={isUrlChangedIgnoringFilters}
            willMount={true}
        >
            <FiltersModalMobile />
        </Modal>

        <Modal name={MODALS.EXPRESS_ORDER.name} willMount={true}>
            <FilterPickupModalProvider>
                <ProductPickUpModal />
            </FilterPickupModalProvider>
        </Modal>
    </>
);
